import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion } from "framer-motion";
import { Instagram, Mail, ArrowDown, Check, Building2, Dumbbell, Music, ShoppingBag, Home, Sparkles, Hand, CreditCard, CircleDot, Zap, Award, Clock, GraduationCap } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets - Updated with new transparent images
const ASSETS = {
  logoWhite: "https://customer-assets.emergentagent.com/job_venue-revenue/artifacts/rcizesv6_AnytimeFumeWhiteLOGO.png",
  machine1: "https://customer-assets.emergentagent.com/job_venue-revenue/artifacts/fvikj88q_Untitled%20design-17.png",
  machine2: "https://customer-assets.emergentagent.com/job_venue-revenue/artifacts/dh05gbdu_Untitled%20design-18.png",
  gym: "https://images.unsplash.com/photo-1637870473618-8c9fa7d11f0a?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  nightclub: "https://images.unsplash.com/photo-1566737236500-c8ac43014a67?w=800&auto=format&fit=crop",
  office: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&auto=format&fit=crop",
  shoppingMall: "https://images.unsplash.com/photo-1758448501002-8c7bb7a7d9ff?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  residential: "https://images.unsplash.com/photo-1758448511533-e1502259fff6?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  university: "https://images.unsplash.com/photo-1670284768187-5cc68eada1b3?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 1, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 }
  }
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToForm = () => {
    document.getElementById("interest-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
      scrolled ? "bg-black/90 nav-blur border-b border-white/5" : "bg-transparent"}`
      }>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <a href="/" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src={ASSETS.logoWhite}
            alt="Anytime Fume"
            className="h-8 md:h-10 w-auto"
            data-testid="logo-image" />

          <span className="hidden sm:block font-heading md:text-2xl !font-['Arial'] !text-xl !tracking-[0.2em] text-white">ANYTIME FUME

          </span>
        </a>
        <div className="flex items-center gap-3 md:gap-4">
          <a
            href="/support"
            data-testid="nav-support-btn"
            className="text-white/70 hover:text-white px-3 md:px-4 py-2.5 text-[10px] md:text-xs font-medium tracking-wider uppercase transition-all duration-300 border border-white/20 hover:border-white/40">
            Support
          </a>
          <button
            onClick={scrollToForm}
            data-testid="nav-interest-btn"
            className="bg-white text-black px-5 md:px-6 py-2.5 text-[10px] md:text-xs font-medium tracking-wider uppercase hover:bg-white/90 transition-all duration-300">
            Register Interest
          </button>
        </div>
      </div>
    </nav>);

};

// Hero Section
const HeroSection = () => {
  const scrollToMachine = () => {
    document.getElementById("machine-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="hero-section" className="min-h-screen relative flex items-center overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(40,40,40,0.3),transparent)]" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center lg:text-left">

            {/* Coming Soon Badge */}
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 border border-white/30 text-white/80 text-[10px] uppercase tracking-[0.2em] font-body">
                Coming Soon to Melbourne
              </span>
            </motion.div>

            {/* Main Tagline */}
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-[5rem] leading-[1] text-white mb-6">

              Smell Luxury<br />
              <span className="italic font-light text-white/90">On The Go</span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="font-body text-base md:text-lg text-white/70 max-w-lg mx-auto lg:mx-0 leading-relaxed mb-8">

              Premium single sprays of designer perfumes, dispensed through our innovative <strong className="text-white">perfume vending machines</strong>. A new amenity for forward-thinking venues.
            </motion.p>

            <motion.div variants={fadeInUp}>
              <button
                onClick={scrollToMachine}
                data-testid="hero-learn-btn"
                className="group inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors font-body text-sm tracking-wide">

                <span>Discover More</span>
                <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
          
          {/* Right - Side by Side Machine Images */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative flex justify-center items-end gap-4 md:gap-6">

            <img
              src={ASSETS.machine1}
              alt="Anytime Fume Perfume Vending Machine"
              className="w-[45%] max-w-[220px] md:max-w-[260px] machine-glow"
              data-testid="hero-machine-image-1" />

            <img
              src={ASSETS.machine2}
              alt="Anytime Fume Perfume Vending Machine"
              className="w-[45%] max-w-[220px] md:max-w-[260px] machine-glow"
              data-testid="hero-machine-image-2" />

          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block">

        <div className="scroll-indicator">
          <div className="w-[1px] h-12 bg-gradient-to-b from-white/30 to-transparent" />
        </div>
      </motion.div>
    </section>);

};

// Ideal Venues Section - Moved Up
const VenuesSection = () => {
  const venues = [
  { name: "Gyms & Sports Centres", image: ASSETS.gym, icon: <Dumbbell className="w-4 h-4" /> },
  { name: "Nightclubs & Bars", image: ASSETS.nightclub, icon: <Music className="w-4 h-4" /> },
  { name: "Corporate Offices", image: ASSETS.office, icon: <Building2 className="w-4 h-4" /> },
  { name: "Shopping Malls", image: ASSETS.shoppingMall, icon: <ShoppingBag className="w-4 h-4" /> },
  { name: "University Campuses", image: ASSETS.university, icon: <GraduationCap className="w-4 h-4" /> },
  { name: "Residential Buildings", image: ASSETS.residential, icon: <Home className="w-4 h-4" /> }];


  return (
    <section id="machine-section" data-testid="venues-section" className="py-24 md:py-32 bg-neutral-950 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="mb-14 text-center">

          <motion.p variants={fadeInUp} className="text-white/60 uppercase tracking-[0.2em] text-xs mb-5 font-body">
            Ideal Venues
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl text-white mb-4">
            Perfect For <span className="italic">High-Traffic</span> Spaces
          </motion.h2>
          <motion.p variants={fadeInUp} className="font-body text-white/70 max-w-2xl mx-auto">
            Our perfume vending machines are designed for venues where people want to look and feel their best.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">

          {venues.map((venue, index) =>
          <motion.div
            key={index}
            variants={fadeInUp}
            data-testid={`venue-card-${index}`}
            className="group relative aspect-[4/5] md:aspect-[3/4] overflow-hidden">

              <img
              src={venue.image}
              alt={venue.name}
              className="venue-image w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />

              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                <div className="flex items-center gap-2.5 text-white">
                  <span className="text-white/70">{venue.icon}</span>
                  <span className="font-body text-xs md:text-sm tracking-wide">{venue.name}</span>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// Venue Benefits Section - Moved Up
const BenefitsSection = () => {
  const benefits = [
  {
    title: "Revenue Share",
    description: "Earn passive monthly income through our transparent revenue sharing model."
  },
  {
    title: "Zero Cost",
    description: "No upfront investment. We cover equipment, installation, and maintenance."
  },
  {
    title: "Fully Managed",
    description: "We handle restocking, servicing, and all operations. You just earn."
  }];


  return (
    <section data-testid="benefits-section" className="py-24 md:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="mb-14">

          <motion.p variants={fadeInUp} className="text-white/60 uppercase tracking-[0.2em] text-xs mb-5 font-body">
            For Venues
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl text-white">
            Why Host Our <span className="italic">Perfume Vending Machine</span>
          </motion.h2>
        </motion.div>
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-0 border border-white/10">

          {benefits.map((benefit, index) =>
          <motion.div
            key={index}
            variants={fadeInUp}
            data-testid={`benefit-card-${index}`}
            className={`benefit-card p-8 md:p-10 ${index < 2 ? 'border-b md:border-b-0 md:border-r border-white/10' : ''}`}>

              <span className="font-heading text-5xl text-white/10 block mb-3">0{index + 1}</span>
              <h3 className="font-heading text-2xl md:text-3xl text-white mb-3">{benefit.title}</h3>
              <p className="font-body text-white/70 leading-relaxed text-sm">{benefit.description}</p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// Machine Features Section
const MachineShowcaseSection = () => {
  const features = [
  { label: "Designer Fragrances", icon: <Sparkles className="w-4 h-4" /> },
  { label: "Additional Revenue Stream", icon: <Award className="w-4 h-4" /> },
  { label: "Instant & Contactless", icon: <Zap className="w-4 h-4" /> },
  { label: "24/7 Convenience", icon: <Clock className="w-4 h-4" /> }];


  return (
    <section data-testid="machine-showcase-section" className="py-24 md:py-32 bg-neutral-950 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Content First */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16">

          <motion.p variants={fadeInUp} className="text-white/60 uppercase tracking-[0.2em] text-xs mb-5 font-body">
            The Machine
          </motion.p>
          
          <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl text-white mb-5 leading-[1.1]">
            Single Sprays of <span className="italic text-white/80">Designer Perfume</span>
          </motion.h2>
          
          <motion.p variants={fadeInUp} className="font-body text-white/70 text-base leading-relaxed max-w-2xl mx-auto mb-10">
            Not bottles—just the perfect spray. Our perfume vending machines dispense premium designer fragrances on demand, giving your guests instant access to luxury scents.
          </motion.p>

          {/* Feature grid - horizontal */}
          <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-6 md:gap-10">
            {features.map((feature, index) =>
            <div
              key={index}
              className="group flex items-center gap-2"
              data-testid={`feature-${index}`}>
                <span className="text-white/60 group-hover:text-white transition-colors">
                  {feature.icon}
                </span>
                <span className="font-body text-sm text-white">{feature.label}</span>
              </div>
            )}
          </motion.div>
        </motion.div>

        {/* Machine Images - Side by Side Below */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col sm:flex-row justify-center items-center sm:items-end gap-6 md:gap-8">

          <img
            src={ASSETS.machine1}
            alt="Anytime Fume Perfume Vending Machine"
            className="h-[250px] sm:h-[280px] md:h-[360px] lg:h-[450px] w-auto object-contain machine-glow"
            data-testid="machine-image-showcase-1" />
          <img
            src={ASSETS.machine2}
            alt="Anytime Fume Perfume Vending Machine"
            className="h-[250px] sm:h-[280px] md:h-[360px] lg:h-[450px] w-auto object-contain machine-glow"
            data-testid="machine-image-showcase-2" />
        </motion.div>
      </div>
    </section>);

};

// How Machine Works Section
const HowMachineWorksSection = () => {
  const steps = [
  {
    number: "01",
    title: "Select",
    description: "Choose from 5 designer fragrances on the HD screen.",
    icon: <CircleDot className="w-5 h-5" />
  },
  {
    number: "02",
    title: "Tap",
    description: "Quick contactless payment via card or phone.",
    icon: <CreditCard className="w-5 h-5" />
  },
  {
    number: "03",
    title: "Position",
    description: "Place your wrist or neck near the dispenser.",
    icon: <Hand className="w-5 h-5" />
  },
  {
    number: "04",
    title: "Press",
    description: "Press the button. Enjoy your luxury spray.",
    icon: <Sparkles className="w-5 h-5" />
  }];


  return (
    <section data-testid="how-machine-works-section" className="py-24 md:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16">

          <motion.p variants={fadeInUp} className="text-white/60 uppercase tracking-[0.2em] text-xs mb-5 font-body">
            How It Works
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl text-white">
            Four Simple Steps
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">

          {steps.map((step, index) =>
          <motion.div
            key={index}
            variants={fadeInUp}
            data-testid={`machine-step-${index}`}
            className="step-card group relative p-6 md:p-8 border border-white/10 bg-neutral-950/50">

              {/* Step number */}
              <span className="absolute top-4 right-4 font-heading text-4xl text-white/10 group-hover:text-white/20 transition-colors">
                {step.number}
              </span>
              
              {/* Icon */}
              <div className="w-9 h-9 flex items-center justify-center text-white/60 mb-6 border border-white/20 group-hover:border-white/40 group-hover:text-white transition-all">
                {step.icon}
              </div>
              
              <h3 className="font-heading text-xl md:text-2xl text-white mb-2">{step.title}</h3>
              <p className="font-body text-white/70 text-xs leading-relaxed">{step.description}</p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// Tagline Banner Section
const TaglineBanner = () => {
  return (
    <section className="py-16 md:py-20 bg-neutral-950 border-y border-white/5">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}>

          <p className="font-heading md:text-4xl lg:text-5xl !text-3xl !italic text-white/90">

          </p>
          <p className="font-body uppercase !font-['Arial'] !text-sm !tracking-[0.1em] mt-4 text-white/30"> Perfume Vending Machines

          </p>
        </motion.div>
      </div>
    </section>);

};

// Trust Section
const TrustSection = () => {
  return (
    <section data-testid="trust-section" className="py-24 md:py-32 bg-black">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}>

          <motion.div variants={fadeInUp} className="mb-8">
            <span className="inline-block px-5 py-2.5 border border-white/20 text-white/70 text-[10px] uppercase tracking-[0.2em] font-body">
              Launching Soon in Melbourne
            </span>
          </motion.div>
          <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-5">
            Anytime Fume Pty Ltd
          </motion.h2>
          <motion.p variants={fadeInUp} className="font-body text-white/70 text-base max-w-xl mx-auto">
            Australia's first perfume vending machine experience—professionally managed, compliance-ready, and designed for premium venues.
          </motion.p>
        </motion.div>
      </div>
    </section>);

};

// Interest Form Section
const InterestFormSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    venue_name: "",
    venue_type: "",
    city: "",
    email: "",
    phone: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, venue_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      setIsSubmitted(true);
      toast.success("Thank you for your interest!");
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="interest-form" data-testid="interest-form-section" className="py-24 md:py-32 bg-neutral-950 border-t border-white/5">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>

            <div className="w-14 h-14 border border-white/20 flex items-center justify-center text-white mx-auto mb-6">
              <Check className="w-6 h-6" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">Thank You</h2>
            <p className="font-body text-white/70 text-base" data-testid="success-message">
              We've received your interest. We'll be in touch when we launch in your area.
            </p>
          </motion.div>
        </div>
      </section>);

  }

  return (
    <section id="interest-form" data-testid="interest-form-section" className="py-24 md:py-32 bg-neutral-950 border-t border-white/5">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="mb-12 text-center">

          <motion.p variants={fadeInUp} className="text-white/60 uppercase tracking-[0.2em] text-xs mb-5 font-body">
            Be The First To Know
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl text-white mb-4">
            Register Your Interest
          </motion.h2>
          <motion.p variants={fadeInUp} className="font-body text-white/70 text-sm max-w-md mx-auto">
            Interested in hosting an Anytime Fume perfume vending machine? Leave your details and we'll contact you when we launch.
          </motion.p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="space-y-6"
          data-testid="interest-form">

          <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-6">
            <div className="form-input-line">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                required
                data-testid="input-name"
                className="w-full bg-transparent border-b border-white/20 focus:border-white px-0 py-3 text-white placeholder:text-white/50 focus:outline-none transition-colors font-body text-sm" />

            </div>
            <div className="form-input-line">
              <input
                type="text"
                name="venue_name"
                value={formData.venue_name}
                onChange={handleChange}
                placeholder="Venue Name"
                required
                data-testid="input-venue-name"
                className="w-full bg-transparent border-b border-white/20 focus:border-white px-0 py-3 text-white placeholder:text-white/50 focus:outline-none transition-colors font-body text-sm" />

            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-6">
            <div>
              <Select onValueChange={handleSelectChange} required>
                <SelectTrigger
                  data-testid="select-venue-type"
                  className="w-full bg-transparent border-0 border-b border-white/15 focus:border-white/60 rounded-none px-0 py-3 text-white focus:ring-0 font-body h-auto text-sm">

                  <SelectValue placeholder="Venue Type" className="text-white/25" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-900 border-white/10">
                  <SelectItem value="gym" data-testid="option-gym">Gym / Sports Centre</SelectItem>
                  <SelectItem value="nightclub" data-testid="option-nightclub">Nightclub / Bar</SelectItem>
                  <SelectItem value="office" data-testid="option-office">Corporate Office</SelectItem>
                  <SelectItem value="shopping" data-testid="option-shopping">Shopping Mall</SelectItem>
                  <SelectItem value="university" data-testid="option-university">University Campus</SelectItem>
                  <SelectItem value="residential" data-testid="option-residential">Residential Building</SelectItem>
                  <SelectItem value="other" data-testid="option-other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-input-line">
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                required
                data-testid="input-city"
                className="w-full bg-transparent border-b border-white/20 focus:border-white px-0 py-3 text-white placeholder:text-white/50 focus:outline-none transition-colors font-body text-sm" />

            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-6">
            <div className="form-input-line">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email Address"
                required
                data-testid="input-email"
                className="w-full bg-transparent border-b border-white/20 focus:border-white px-0 py-3 text-white placeholder:text-white/50 focus:outline-none transition-colors font-body text-sm" />

            </div>
            <div className="form-input-line">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone Number"
                required
                data-testid="input-phone"
                className="w-full bg-transparent border-b border-white/20 focus:border-white px-0 py-3 text-white placeholder:text-white/50 focus:outline-none transition-colors font-body text-sm" />

            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="pt-4 text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="submit-btn"
              className="btn-primary bg-white text-black px-12 py-3.5 text-xs font-medium tracking-wider uppercase hover:bg-white/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">

              {isSubmitting ? "Submitting..." : "Submit Interest"}
            </button>
          </motion.div>
        </motion.form>
      </div>
    </section>);

};

// Footer Section
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 bg-black border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col items-center gap-5 mb-10">
          <div className="flex items-center gap-3">
            <img
              src={ASSETS.logoWhite}
              alt="Anytime Fume"
              className="h-10 w-auto" />
            <span className="font-body text-xl tracking-[0.15em] text-white">ANYTIME FUME</span>
          </div>
          <p className="font-body text-sm text-white/60 tracking-wide">
            Premium Perfume Vending Machines
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-5 py-6 border-t border-white/10">
          <div className="text-center md:text-left">
            <span className="text-white/60 text-xs font-body block">
              Anytime Fume Pty Ltd
            </span>
            <span className="text-white/40 text-[10px] font-body">
              ABN: 25 693 934 217
            </span>
          </div>
          
          <div className="flex items-center gap-6">
            <a
              href="/support"
              data-testid="footer-support-link"
              className="link-underline flex items-center gap-2 text-white/60 hover:text-white transition-colors font-body text-xs">
              Customer Support
            </a>
            <a
              href="mailto:tsyon@anytimefume.com.au"
              data-testid="footer-email-link"
              className="link-underline flex items-center gap-2 text-white/60 hover:text-white transition-colors font-body text-xs">
              <Mail className="w-3.5 h-3.5" />
              tsyon@anytimefume.com.au
            </a>
            <a
              href="https://instagram.com/anytimefume"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="link-underline flex items-center gap-2 text-white/60 hover:text-white transition-colors font-body text-xs">
              <Instagram className="w-3.5 h-3.5" />
              Instagram
            </a>
          </div>
        </div>
        
        <div className="pt-6 text-center">
          <p className="text-white/40 text-[10px] font-body">
            &copy; {new Date().getFullYear()} Anytime Fume Pty Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>);

};

// Main App Component
function PartnerPage() {
  return (
    <div className="App bg-black min-h-screen">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: '#0a0a0a',
            color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.08)',
            fontFamily: 'DM Sans, sans-serif'
          }
        }} />

      <Navigation />
      <main>
        <HeroSection />
        <VenuesSection />
        <BenefitsSection />
        <MachineShowcaseSection />
        <HowMachineWorksSection />
        <TaglineBanner />
        <TrustSection />
        <InterestFormSection />
      </main>
      <Footer />
    </div>);

}

export default PartnerPage;